/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import {
    Component,
    Input,
} from '@angular/core';

import * as globalL10n from 'global-l10n';
import { L10nService } from '@vmw/ngx-vip';
import { LocationHdrMatch } from 'object-types';
import { SchemaService } from 'ajs/modules/core/services/schema-service';
import { HostHdrMatchConfigItem }
    from 'ajs/modules/match/factories/host-hdr-match.config-item.factory';

const { ENGLISH: dictionary, ...globalL10nKeys } = globalL10n;

/**
 * @description Component to show readonly information for host-hdr-match and loc-hdr-match.
 * @author Akul Aggarwal
 */
@Component({
    selector: 'host-hdr-match-readonly',
    templateUrl: './host-hdr-match-readonly.component.html',
})
export class HostHdrMatchReadonlyComponent {
    /**
     * HostHdrMatchConfigItem instance.
     */
    @Input()
    public editable: HostHdrMatchConfigItem;

    /**
     * Get keys from global source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    constructor(
        private readonly l10nService: L10nService,
        private readonly schemaService: SchemaService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Convenience method to get text label for selected match criteria.
     */
    public get matchCriteria(): string {
        return this.schemaService.getEnumValueLabel(
            'HdrMatchOperation',
            this.editable.config.match_criteria,
        );
    }

    /**
     * Returns list of values, if any, corresponding to criteria.
     */
    public get values(): string[] | undefined {
        return this.editable.config.value;
    }

    /**
     * Gets the match label based on the match message type.
     */
    public get matchLabel(): string {
        const { hostHeaderLabel, locationHeaderLabel } = globalL10nKeys;
        const key = this.editable.messageType ===
            LocationHdrMatch ? locationHeaderLabel : hostHeaderLabel;

        return this.l10nService.getMessage(key);
    }

    /**
     * Used in *ngFor.
     */
    public trackByIndex(index: number): number {
        return index;
    }
}
